import React, { type FC, type PropsWithChildren, use, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AgentRunContext } from "./agentRunContext";
import { AgentRunSession } from "./BinaleAgentModal";
import { AGENT_STEPS } from "./steps";
import type { AgentContext, AgentStep } from "./runner/types";
import type { AgentRun } from "./useAgentRun";
import { CompanyContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";
import { ErrorBoundary } from "@domain/routing/ErrorBoundary";

export { AgentRunContext, type AgentRunContextValue } from "./agentRunContext";

interface Session {
    id: number;
    isOpen: boolean;
    companyId: string;
    context?: AgentContext;
}

interface Props {
    /** Defaults to the full scenario; tests pass their own. */
    steps?: AgentStep[];
    /** Builds the context of each run; defaults to `useAgentContext()` inside the run. Tests pass a fake. */
    makeContext?: () => AgentContext;
}

/** Holds the one Binale Agent run above the routes, so it survives navigation as the modal or the floating pill. */
export const AgentRunProvider: FC<PropsWithChildren<Props>> = ({ children, steps = AGENT_STEPS, makeContext }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [run, setRun] = useState<AgentRun | null>(null);
    const counterRef = useRef(0);
    const user = use(UserContext);
    const companyId = use(CompanyContext).companyGQL?.id ?? "";
    const allowed = Boolean(user.fireUser) && user.isAdminWrite === true && companyId !== "";
    // Sign-out, lost rights or another company end the run; it never continues for someone else.
    const live = session !== null && allowed && session.companyId === companyId;

    // Before the session first reports its run, run is null and the run counts as active.
    const isActive = live && run?.state !== "finished";
    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;
    const scopeRef = useRef({ allowed, companyId });
    scopeRef.current = { allowed, companyId };

    useEffect(() => {
        if (session && !live) {
            setSession(null);
            setRun(null);
        }
    }, [session, live]);

    const open = useCallback(() => {
        if (!scopeRef.current.allowed) {
            return;
        }
        if (isActiveRef.current) {
            setSession((s) => s && { ...s, isOpen: true });
            return;
        }
        setRun(null);
        setSession({
            id: ++counterRef.current,
            isOpen: true,
            companyId: scopeRef.current.companyId,
            context: makeContext?.(),
        });
    }, [makeContext]);
    const restore = useCallback(() => setSession((s) => s && { ...s, isOpen: true }), []);
    const minimize = useCallback(() => setSession((s) => s && { ...s, isOpen: false }), []);
    const close = useCallback(() => {
        setSession(null);
        setRun(null);
    }, []);

    const value = useMemo(
        () => ({
            isOpen: live && (session?.isOpen ?? false),
            isActive,
            hasRun: live,
            open,
            minimize,
            close,
            run: live ? run : null,
        }),
        [live, session?.isOpen, isActive, open, minimize, close, run],
    );

    return (
        <AgentRunContext value={value}>
            {children}
            {session && live ? (
                // A render error in the agent ends the run; the app around it keeps working.
                <ErrorBoundary key={session.id} setError={close}>
                    <AgentRunSession
                        isOpen={session.isOpen}
                        steps={steps}
                        context={session.context}
                        onClose={close}
                        onMinimize={minimize}
                        onRestore={restore}
                        onRun={setRun}
                    />
                </ErrorBoundary>
            ) : null}
        </AgentRunContext>
    );
};
